// Inline "New Item" modal — lifted verbatim from the old /items/new page
// so the whole tracker lives on one URL. Opened from the New Item button
// on the Dev & Bug Tracker page; on submit it navigates to the created
// item's detail page (same UX as before).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import RichEditor from "../components/RichEditor";
import { X, Loader2 } from "lucide-react";
import { TYPES, PRIORITIES, TALMIA_META, TYPE_META } from "../constants/enums";

export default function NewItemModal({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", type: "Bug", priority: "Medium",
    talmia_version: "2", assigned_to: "",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
    setForm({ title: "", description: "", type: "Bug", priority: "Medium", talmia_version: "2", assigned_to: "" });
    setErr("");
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.title.trim()) { setErr("Title is required."); return; }
    setSaving(true);
    try {
      const payload = { ...form, assigned_to: form.assigned_to || null };
      const { data } = await api.post("/items", payload);
      onCreated?.(data);
      onClose?.();
      // Navigate to detail so RichEditor images etc. persist naturally.
      navigate(`/items/${data.id}`);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Failed to create item");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" data-testid="new-item-modal" onClick={() => !saving && onClose?.()}>
      <div className="w-full max-w-2xl fade-up" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="card-dark p-6 space-y-5" data-testid="new-item-form">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-display text-xl font-bold">Log an item</h3>
              <p className="text-xs text-slate-400 mt-0.5">Bug, feature, improvement, chore or incident — anything on the roadmap.</p>
            </div>
            <button type="button" onClick={onClose} disabled={saving} className="w-8 h-8 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-40" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-300">System *</label>
            <div className="mt-1.5 flex gap-2 flex-wrap">
              {["2", "1", "both"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setForm({ ...form, talmia_version: v })}
                  data-testid={`new-item-talmia-${v}`}
                  className={`px-3 py-1.5 rounded-md border text-xs font-mono uppercase tracking-widest ${
                    form.talmia_version === v ? TALMIA_META[v].tone : "border-white/10 bg-white/[0.02] text-slate-400 hover:text-white"
                  }`}
                >
                  {TALMIA_META[v].label} {v === "2" ? "· Talmia 2 (new)" : v === "1" ? "· Talmia 1 (legacy)" : "· Cross-system"}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-300">Title *</label>
            <input
              className="input-dark mt-1.5"
              placeholder="Login button does not work on mobile"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              required maxLength={200} autoFocus data-testid="new-item-title-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-300">Type *</label>
              <select className="input-dark mt-1.5" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="new-item-type-select">
                {TYPES.map((t) => <option key={t} value={t}>{TYPE_META[t]?.icon || ""} {t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-300">Priority</label>
              <select className="input-dark mt-1.5" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="new-item-priority-select">
                {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-300">Assign To</label>
              <select className="input-dark mt-1.5" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} data-testid="new-item-assignee-select">
                <option value="">Unassigned</option>
                {users.map((u) => <option key={u.id} value={u.id}>{`${u.name} (${u.email})`}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-300 block mb-1.5">Description</label>
            <RichEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder="Steps to reproduce, screenshots, expected vs actual..." testId="new-item-description" />
          </div>

          {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="new-item-error">{err}</div>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm border border-white/10 hover:bg-white/5" data-testid="new-item-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-coral inline-flex items-center gap-2" data-testid="new-item-submit">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? "Creating…" : "Create item"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
